import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import cx from "classnames";
import React from "react";

import { counsellingsNewUrl } from "@/mk2/apps/counsellings/urls";
import { StrollerCardPlaceholder } from "@/mk2/apps/strollers/components/StrollerCard";
import { StrollersBuyingGuides } from "@/mk2/apps/strollers/components/StrollersBuyingGuides";
import { StrollersList } from "@/mk2/apps/strollers/components/StrollersList";
import { Props } from "@/mk2/apps/strollers/containers/BrandsStrollersList/BrandsStrollersListPage";
import { LinkedArticles } from "@/mk2/apps/strollers/containers/StrollersDetail/LinkedArticles";
import { LinkedObjects } from "@/mk2/apps/strollers/containers/StrollersDetail/LinkedObjects";
import { PageMode } from "@/mk2/apps/strollers/containers/StrollersSearch/StrollersSearch.constants";
import { strollersSearchUrl, strollersUrl } from "@/mk2/apps/strollers/urls";
import { BreadcrumbSiteStructure } from "@/mk2/components/BreadcrumbSiteStructure";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { ExpandableClamp } from "@/mk2/components/ExpandableClamp";
import { ImgVarHeight } from "@/mk2/components/ImgVarHeight";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { LoadingPlaceholder } from "@/mk2/components/LoadingPlaceholder";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { astToHtml, defaultFilterAdditionalData } from "@/mk2/helpers/article_ast/filters";
import { cacheLast } from "@/mk2/helpers/cache";
import { url } from "@/mk2/helpers/urls";
import { LoadingState } from "@/mk2/reducers/loadingState";
import { interpolate } from "@/mk2/services/i18n";
import {
    STROLLERS_NO_RESULTS,
    STROLLERS_SEARCH_HOMEPAGE_BTN_TITLE,
    STROLLER_ADD_QUESTION,
    STROLLER_BRAND_LINKED_ARTICLES_OF_brand,
    STROLLER_BRAND_LINKED_OBJECTS_OF_brand,
    STROLLER_MORE_ABOUT_BRAND,
    STROLLER_OFFICIAL_SITE_link,
    STROLLER_STROLLEROPEDIA,
} from "@/mk/autogenerated/translation";

import styles from "./BrandsStrollersList.mscss";

export class BrandsStrollersList extends React.Component<Props> {
    private astToHtmlCache = cacheLast(true);

    public render() {
        const { brand, loadingState } = this.props;

        return (
            <div className={styles.BrandsStrollersList}>
                <BreadcrumbSiteStructure
                    items={this.getBreadcrumbItems()}
                    className={styles.BrandsStrollersList__breadcrumb}
                />
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderLoadSuccess}
                    hasData={!this.props.slug || !!(this.props.slug && brand)}
                />
            </div>
        );
    }

    private renderInit = () => {
        return (
            <>
                <div className={styles.BrandsStrollersList__header}>
                    <h1 className={styles.BrandsStrollersList__header__heading}>
                        <LoadingPlaceholder width={180} height={26} />
                    </h1>
                    <div className={styles.BrandsStrollersList__header__logoBox}>
                        <LoadingPlaceholder width={125} height={50} />
                    </div>
                </div>

                <div className={styles.BrandsStrollersList__descBox}>
                    <LoadingPlaceholder width="100%" height={200} />
                </div>

                <div className={styles.BrandsStrollersList__items}>
                    {Array.from(Array(8).keys()).map((index) => (
                        <div key={index} className={styles.BrandsStrollersList__items__container}>
                            <StrollerCardPlaceholder
                                className={styles.BrandsStrollersList__items__container__item}
                            />
                        </div>
                    ))}
                </div>
            </>
        );
    };

    private renderLoadSuccess = () => {
        const {
            articles,
            brand,
            buyingGuide,
            count,
            hasMore,
            strollers,
            strollersPreviewPhotoMap,
            strollersPhotosMap,
            loadingState,
            onStrollersComparisonToggle,
            comparison,
            counselling,
            forumPostLinkedObjects,
        } = this.props;

        const logoPhoto = brand.logoPhoto;

        let brandDescription = "";
        if (brand.descriptionAST) {
            const additionalData = {
                ...defaultFilterAdditionalData,
                linkedObjects: brand.descriptionASTAdditionalData
                    ? brand.descriptionASTAdditionalData.linkedObjects
                    : {},
                renderSpecialWikiLinks: false,
            };
            brandDescription = this.astToHtmlCache(
                () => astToHtml(brand.descriptionAST, additionalData),
                brand.descriptionAST,
            );
        }

        return (
            <>
                <div className={styles.BrandsStrollersList__header}>
                    <h1 className={styles.BrandsStrollersList__header__heading}>{brand.name}</h1>
                    {logoPhoto && (
                        <div className={styles.BrandsStrollersList__header__logoBox}>
                            <ImgVarHeight
                                width={125}
                                imgWidth={logoPhoto.originalWidth}
                                imgHeight={logoPhoto.originalHeight}
                                src={logoPhoto.url250x250}
                            />
                        </div>
                    )}
                </div>

                {brandDescription && (
                    <div className={styles.BrandsStrollersList__descBox}>
                        <ExpandableClamp expandLabel={STROLLER_MORE_ABOUT_BRAND} clamp={5}>
                            <div>
                                <div
                                    className={cx(
                                        styles.BrandsStrollersList__descBox__desc,
                                        !logoPhoto &&
                                            styles["BrandsStrollersList__descBox__desc--noLogo"],
                                    )}
                                >
                                    {brandDescription}
                                </div>
                                {brand.brandUrl && (
                                    <div
                                        className={styles.BrandsStrollersList__descBox__moreInfoAt}
                                    >
                                        <Interpolate
                                            i18nKey={STROLLER_OFFICIAL_SITE_link}
                                            link={
                                                <a
                                                    data-google-interstitial="false"
                                                    href={brand.brandUrl}
                                                    target="_blank"
                                                    rel="noopener"
                                                >
                                                    {brand.name}
                                                </a>
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </ExpandableClamp>
                    </div>
                )}
                {buyingGuide && (
                    <StrollersBuyingGuides
                        buyingGuideContentItem={buyingGuide}
                        className={styles.BrandsStrollersList__buyingGuides}
                    />
                )}

                <div className={styles.BrandsStrollersList__search}>
                    <Btn
                        type={BtnType.Blue}
                        icon={faSearch}
                        link={url(strollersSearchUrl, { pageMode: PageMode.SHOW_SEARCH_FORM })}
                        label={STROLLERS_SEARCH_HOMEPAGE_BTN_TITLE}
                    />
                </div>

                <StrollersList
                    onComparisonToggle={onStrollersComparisonToggle}
                    loadingState={loadingState}
                    items={strollers}
                    hasMore={hasMore}
                    count={count}
                    strollersPreviewPhotoMap={strollersPreviewPhotoMap}
                    variantsPreviewPhotoMap={{}}
                    photoObjectsMap={strollersPhotosMap}
                    noResultMessage={STROLLERS_NO_RESULTS}
                    comparison={comparison}
                    loadNext={this.loadNext}
                    load={this.onLoadList}
                />
                {articles?.length > 0 && (
                    <LinkedArticles
                        className={styles.BrandsStrollersList__linkedArticles}
                        articles={articles}
                        header={interpolate(STROLLER_BRAND_LINKED_ARTICLES_OF_brand, {
                            brand: brand.name,
                        })}
                    />
                )}
                {forumPostLinkedObjects?.length > 0 && (
                    <LinkedObjects
                        className={styles.BrandsStrollersList__linkedObjects}
                        header={interpolate(STROLLER_BRAND_LINKED_OBJECTS_OF_brand, {
                            brand: brand.name,
                        })}
                        linkedObjects={forumPostLinkedObjects}
                        addButton={
                            counselling ? (
                                <Btn
                                    label={STROLLER_ADD_QUESTION}
                                    link={url(counsellingsNewUrl, {
                                        topicSlug: counselling.topic.slug,
                                    })}
                                />
                            ) : null
                        }
                    />
                )}
            </>
        );
    };

    private onLoadList = () => {
        // We are already loaded from first LoadingSwitch
        // so ignore
    };

    private load = () => {
        this.props.onLoad(this.props.slug);
        this.props.onStrollersComparisonLoad();
    };

    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { strollers, onLoad } = this.props;

        onLoad(this.props.slug, strollers.length);

        // Debounce loadMoreRows
        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private getBreadcrumbItems = () => {
        const { brand } = this.props;
        const breadcrumbLinks = [];
        breadcrumbLinks.push(
            <Link className={styles.BreadcrumbItem} key="strolleropedia" to={strollersUrl}>
                {STROLLER_STROLLEROPEDIA}
            </Link>,
        );
        breadcrumbLinks.push(<>{brand?.name || "..."}</>);
        return breadcrumbLinks;
    };
}
